/**
 * The one crossing between Preact and the Mapbox custom elements.
 *
 * Four islands used to reach the map on their own terms: `getElementById` with
 * an id passed down as a prop, `addEventListener` with a bare string, a
 * `CustomEvent<{...}>` cast restating a payload declared elsewhere, and — on
 * the public globe — a hand-rolled stylesheet load racing the element's own.
 * Nothing type-checked, and none of it could be tested without WebGL.
 *
 * Everything crossing goes through here now. Readiness is one awaited
 * condition, subscription is typed off `MapEventDetailMap`, and the JSON
 * attributes are serialized by the same module the elements parse them with.
 * `just test::rules` keeps it that way: a map event name written as a string
 * literal, or a map element resolved from the document, fails outside this
 * directory.
 *
 * Mapbox-free by construction — an island importing this must not pull 489 KB
 * of `mapbox-gl` into its graph, so the map elements are described
 * structurally rather than imported from `lib/web-components/`.
 */

import type { TripFilter } from "../trips/filter.ts"
import {
  MAP_EVENT,
  type MapEventDetailMap,
  type MapEventName,
  type MapPoi,
  type MapRoute,
  type MapTrip,
  type MapTripSummary,
  type SerializedJson,
} from "./types.ts"

export * from "./types.ts"

// Type-only, so the turf dependency behind `camera.ts` never reaches an
// island bundle — the same reason this module stays clear of `mapbox-gl`.
export type { LightPreset } from "./camera.ts"
import type { LightPreset } from "./camera.ts"

// -----------------------------------------------------------------------------
// Element shapes
// -----------------------------------------------------------------------------

/** What every map element offers, however it renders. */
export interface MapElement extends HTMLElement {
  /**
   * True once the Mapbox instance exists and its controls are mounted — the
   * same moment `map-ready` fires. Set by `MapControlHost`, so it is `undefined`
   * on an element the browser has not upgraded yet.
   */
  readonly mapReady?: boolean
  readonly isMoving: boolean
  /**
   * Fly the camera; `false` when no flight was issued — the element is not
   * built yet, or a flight is already in the air. A caller that waits on
   * `move-end` must branch on this, or it waits for a settle that never comes.
   */
  flyTo(
    lat: number,
    lng: number,
    options?: {
      /** Omitted holds the viewer's zoom — a flight with no opinion re-frames nothing. */
      zoom?: number
      speed?: number
      offset?: [number, number]
      /** Degrees off vertical. Omitted leaves the camera where the viewer put it. */
      pitch?: number
      /** Degrees clockwise from north. Omitted holds the current heading. */
      bearing?: number
    },
  ): boolean
  /** Refit the viewport to the whole trip — the framing the map first loads with. */
  resetViewport(): void
}

/** `<trip-map>` — a trip's route and its POIs. */
export interface TripMapElement extends MapElement {
  /**
   * Fly to a POI and halo its marker. `null` clears the halo and issues no
   * flight — the Lead-in and a POI with no coordinates, which a bare id cannot
   * express. Returns whether a flight was issued, as `flyTo` does.
   */
  flyToPoi(
    id: string | null,
    options?: {
      /**
       * Overrides the framing entirely. Omitted, a cinematic flight takes
       * `camera.ts`'s zoom carrying the viewer's own zoom adjustment, and a
       * `flat` one keeps the viewer's zoom outright.
       */
      zoom?: number
      speed?: number
      offset?: [number, number]
      /**
       * Skip the cinematic framing — no pitch, no turn, no relighting. The
       * flight is otherwise identical.
       */
      flat?: boolean
    },
  ): boolean
  /**
   * Drift the camera around the subject for `ms`, without re-centring.
   *
   * Not a flight, and its `move-end` is not an arrival — the caller that
   * started it is responsible for telling the two apart, which is why this
   * reports whether one started. `false` means no `move-end` is coming from
   * it, so a caller holding a "discount the next settle" flag must not arm it.
   */
  orbit(ms: number): boolean
  /** Stop any camera motion immediately, so the next `flyTo` is not dropped. */
  stopMotion(): void
  /** Light the basemap for a moment in the trip; `null` restores the default. */
  setLight(preset: LightPreset | null): void
  /** Show or hide the basemap's own place-of-interest labels. */
  setBasemapPoiLabels(visible: boolean): void
  /**
   * Draw only the marker the camera is on, hiding the rest.
   *
   * For playback, where a halo has nothing to stand out against inside a
   * cluster. Ignored while the camera has no POI, so the Lead-in still shows
   * the whole trip.
   */
  setSoloMarker(solo: boolean): void
  /**
   * Replace the rendered POIs without re-framing the map — the live-edit door.
   *
   * The `pois` attribute belongs to the page render; writing an edit back
   * through it refits the viewport and moves the camera off whatever the
   * viewer was looking at.
   */
  setPois(json: SerializedJson<MapPoi[]>): void
}

/** `<globe-view>` — every trip as a marker, filterable. */
export interface GlobeElement extends MapElement {
  setFilter(filter: TripFilter | null): void
}

/**
 * The document surface the bridge uses.
 *
 * Named so a test can pass a fake: the bridge is the piece that must work
 * without Mapbox, WebGL or a built bundle, and `Document` is assignable to
 * this by construction.
 */
export type MapDocument = Pick<
  Document,
  | "getElementById"
  | "querySelector"
  | "createElement"
  | "head"
  | "addEventListener"
  | "removeEventListener"
>

export interface MapBridgeOptions {
  /** Abort the wait — pass an effect's controller signal. */
  signal?: AbortSignal
  /** Test seam. Defaults to the real document. */
  doc?: MapDocument
}

function resolveDoc(doc?: MapDocument): MapDocument {
  return doc ?? (globalThis.document as unknown as MapDocument)
}

// -----------------------------------------------------------------------------
// Assets
// -----------------------------------------------------------------------------

const MAPBOX_CSS = "https://api.mapbox.com/mapbox-gl-js/v3.9.0/mapbox-gl.css"
const MAP_BUNDLE = "/components-map.js"

/**
 * Load the map bundle and Mapbox's stylesheet, and wait for them.
 *
 * `_app.tsx` already emits both on the routes `needsMap()` covers, so this is
 * normally a check that finds the work done. It matters on the public globe:
 * there the element is created after a visitor clears the bot challenge, and
 * Mapbox looks for its CSS synchronously as the map is constructed — losing
 * that race is a map that renders wrong. `PublicGlobeGate` used to close it
 * with its own copy of this function; there is one copy now, and it is here.
 *
 * Resolves on a stylesheet error too. A missing stylesheet is cosmetic, and
 * refusing to show the map over it is the worse failure.
 */
export function ensureMapAssets(doc?: MapDocument): Promise<void> {
  const d = resolveDoc(doc)
  return Promise.all([ensureStylesheet(d), ensureBundle(d)]).then(() => {})
}

/**
 * Mapbox's stylesheet, once per document.
 *
 * The map elements call this from `connectedCallback` — Mapbox needs its CSS
 * present, and the element is the one thing guaranteed to be on any page that
 * has a map. `ensureMapAssets` above is the caller-facing form, which also
 * covers the bundle; the elements only ever need the stylesheet, because the
 * bundle is what they arrived in.
 */
export function ensureMapStylesheet(doc?: MapDocument): Promise<void> {
  return ensureStylesheet(resolveDoc(doc))
}

function ensureStylesheet(d: MapDocument): Promise<void> {
  const existing = d.querySelector<HTMLLinkElement>("[data-mapbox-css]")
  if (existing) {
    if (existing.sheet) return Promise.resolve()
    return settled(existing)
  }
  const link = d.createElement("link") as HTMLLinkElement
  link.rel = "stylesheet"
  link.href = MAPBOX_CSS
  link.dataset.mapboxCss = ""
  const done = settled(link)
  d.head.appendChild(link)
  return done
}

function ensureBundle(d: MapDocument): Promise<void> {
  const existing = d.querySelector<HTMLScriptElement>(
    `script[src="${MAP_BUNDLE}"]`,
  )
  // A module script already in the markup may have run long ago, and a `load`
  // listener added afterwards never fires. Readiness is settled by the element
  // itself, not by this tag, so its presence is enough.
  if (existing) return Promise.resolve()
  const script = d.createElement("script") as HTMLScriptElement
  script.type = "module"
  script.src = MAP_BUNDLE
  const done = settled(script)
  d.head.appendChild(script)
  return done
}

/** Resolve on `load` or `error` — either way the wait is over. */
function settled(el: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    el.addEventListener("load", () => resolve(), { once: true })
    el.addEventListener("error", () => resolve(), { once: true })
  })
}

// -----------------------------------------------------------------------------
// Readiness
// -----------------------------------------------------------------------------

/**
 * Resolve with the map element `id` once it is usable — assets loaded, element
 * upgraded, Mapbox instance built and controls mounted.
 *
 * Resolves `null` when the wait is aborted, so a caller that unmounts first
 * has nothing to catch. It also simply never resolves when the page has no map
 * at that id, which is the honest answer: a trip with no located POIs renders
 * its toolbar and panel without a `<trip-map>`, and the map wiring should not
 * install.
 *
 * Two paths, because either can win. The element may already be ready — it is
 * in the server-rendered markup on most map routes and the bundle is in the
 * head, so `map-ready` can fire before an island hydrates. Or it may not exist
 * yet, as on the public globe. `map-ready` bubbles to the document, so waiting
 * there covers an element that has not been created at the time of the call.
 */
export function whenMapReady<T extends MapElement = MapElement>(
  id: string,
  options: MapBridgeOptions = {},
): Promise<T | null> {
  const d = resolveDoc(options.doc)
  const { signal } = options

  return ensureMapAssets(d).then(() => {
    if (signal?.aborted) return null

    const existing = d.getElementById(id) as T | null
    if (existing?.mapReady) return existing

    return new Promise<T | null>((resolve) => {
      const finish = (value: T | null) => {
        d.removeEventListener(MAP_EVENT.mapReady, onReady)
        signal?.removeEventListener("abort", onAbort)
        resolve(value)
      }
      const onReady = (e: Event) => {
        const target = e.target as T | null
        if (target?.id === id) finish(target)
      }
      const onAbort = () => finish(null)

      d.addEventListener(MAP_EVENT.mapReady, onReady)
      signal?.addEventListener("abort", onAbort, { once: true })
    })
  })
}

/**
 * Run `setup` once the map at `id` is ready; return a cleanup for the effect.
 *
 * The shape every coordinator island wants: `useEffect(() => withMap(id, map =>
 * …), [id])`. `setup` may return its own teardown, which runs on cleanup — and
 * cleaning up before the map ever arrives aborts the wait instead.
 */
export function withMap<T extends MapElement = MapElement>(
  id: string,
  setup: (map: T) => void | (() => void),
  options: Omit<MapBridgeOptions, "signal"> = {},
): () => void {
  const controller = new AbortController()
  let teardown: (() => void) | void

  void whenMapReady<T>(id, { ...options, signal: controller.signal }).then(
    (map) => {
      if (!map || controller.signal.aborted) return
      teardown = setup(map)
    },
  )

  return () => {
    controller.abort()
    teardown?.()
  }
}

// -----------------------------------------------------------------------------
// Events
// -----------------------------------------------------------------------------

/**
 * Subscribe to a map event; returns the unsubscribe.
 *
 * The handler's payload is derived from the name, so a misspelled event has no
 * overload to match and a renamed field has no property to read — the failure
 * the old `el.addEventListener("poi-focused", …)` plus a hand-written
 * `CustomEvent<{ id: string }>` cast could not produce.
 */
export function onMapEvent<K extends MapEventName>(
  el: EventTarget,
  name: K,
  handler: (
    detail: MapEventDetailMap[K],
    event: CustomEvent<MapEventDetailMap[K]>,
  ) => void,
): () => void {
  const listener = (e: Event) => {
    const event = e as CustomEvent<MapEventDetailMap[K]>
    handler(event.detail, event)
  }
  el.addEventListener(name, listener)
  return () => el.removeEventListener(name, listener)
}

// -----------------------------------------------------------------------------
// Attribute serializers
// -----------------------------------------------------------------------------

export function serializeMapPois(
  pois: readonly MapPoi[],
): SerializedJson<MapPoi[]> {
  return JSON.stringify(pois)
}

export function serializeMapTrips(
  trips: readonly MapTrip[],
): SerializedJson<MapTrip[]> {
  return JSON.stringify(trips)
}

export function serializeMapTripSummaries(
  trips: readonly MapTripSummary[],
): SerializedJson<MapTripSummary[]> {
  return JSON.stringify(trips)
}

/**
 * A route attribute. `null` serializes to the empty string, which is what the
 * element treats as "no route" — `"null"` would parse as a value.
 */
export function serializeMapRoute(
  route: MapRoute | null | undefined,
): SerializedJson<MapRoute> {
  return route ? JSON.stringify(route) : ""
}
